import React, {useRef} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {display, fadeInAnimation, flex, m, mb, ml, mr, mt, pointer, pv, w100} from "@pg-design/helpers-css";
import {BrandBuildingCubeIcon, BrandMapPinIcon, BrandPropertyPlanIcon, GalleryOutlineIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";

interface IProps {
    offer: IPropertyDetailOffer;
    property?: IPropertyDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    // offer plans
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
    navBarContentStyle?: string;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyNavBar = React.memo((props: IProps) => {
    const {property, offer, onButtonClick} = props;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const wrapper = useRef<HTMLTableSectionElement>(null);
    const isNavBarSticky = useStickyIntersection(wrapper);

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const planImage = head(property?.plan_image_pages || [])?.image;

    const onShowPropertyGalleryClick = () => {
        props.onButtonClick(OfferModalActiveTab.GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowPropertyPlanClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_DETAIL, offer, offer.vendor);
        props.onButtonClick(
            OfferModalActiveTab.PLAN,
            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
        );
    };

    const renderHeader = () => {
        if (props.property) {
            return (
                <>
                    {isApartment ? "Apartament" : getOfferTypeNameCapital(props.offer.type)}, {props.property.number}, {props.property.rooms}{" "}
                    {roomPlural(props.property.rooms)}, {areaFormat(props.property.area, {precision: 2})}
                </>
            );
        }

        return <span className="bt">{props.offer.name}</span>;
    };

    const showPlanButton = !isMobile && offer.configuration.show_offer_plans && props.isPlanScriptLoading != null && props.onPlansOpenClick != null;

    return (
        <NavigationWrapper className={props.className} ref={wrapper} showBackground={isNavBarSticky}>
            <OneColumnGridHolder className={props.navBarContentStyle}>
                <OneColumnGridMain>
                    <nav css={[navStyles, !isNavBarSticky && justifyRight]}>
                        <div css={[opacity0, display("none"), isNavBarSticky && !isMobile && showHeader]}>
                            <Text as="h3" variant="headline_6">
                                {renderHeader()}
                            </Text>

                            <address css={addressHolder}>
                                <MapMarkerLocationIcon css={mr(2)} fill={theme.colors.gray["700"]} size="2.4" />
                                <Text color={theme.colors.gray[700]} variant="body_copy_2">
                                    {props.offer.address}
                                </Text>
                            </address>
                        </div>

                        <div css={buttonsWrapper}>
                            <Hidden on="desktop">
                                {property && isPropertyFavouriteButtonAvailable(property) && (
                                    <FavoriteButton css={[w100, mb(3)]} offer={offer} property={property} display={{type: "button", size: "m"}} />
                                )}
                                {property && (
                                    <div css={w100}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <StyledButton iconLeft={GalleryOutlineIcon} onClick={onShowPropertyGalleryClick} size="big" variant="brand_icon">
                                                Zobacz galerię
                                            </StyledButton>
                                        </div>
                                    </div>
                                )}
                            </Hidden>
                            <Hidden on="mobile">
                                {property && property.plan && (
                                    <div css={w100}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <StyledButton
                                                href={!planImage ? property.plan : undefined}
                                                iconLeft={BrandPropertyPlanIcon}
                                                onClick={planImage && onShowPropertyPlanClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_PLAN_BUTTON}
                                            >
                                                Zobacz rzut
                                            </StyledButton>
                                        </div>
                                    </div>
                                )}
                            </Hidden>

                            {showPlanButton && (
                                <div css={w100}>
                                    <StyledButton iconLeft={BrandBuildingCubeIcon} onClick={props.onPlansOpenClick} size="big" variant="brand_icon">
                                        {props.isPlanScriptLoading ? "loader" : "Plan pięter"}
                                    </StyledButton>
                                </div>
                            )}

                            <div css={w100}>
                                <StyledButton
                                    iconLeft={BrandMapPinIcon}
                                    onClick={() => {
                                        onButtonClick(
                                            OfferModalActiveTab.MAP,
                                            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        );
                                    }}
                                    size="big"
                                    variant="brand_icon"
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                >
                                    Zobacz na mapie
                                </StyledButton>
                            </div>
                        </div>
                    </nav>
                </OneColumnGridMain>
            </OneColumnGridHolder>
        </NavigationWrapper>
    );
});

const NavigationWrapper = styled.nav<{showBackground: boolean}>`
    display: block;
    z-index: 10; // z-index: we have relative positioned elements on the page, in case of property view, nav bar needs to have z-index: 2, because child components are using default slick slider arrows with index: 1
    width: 100%;
    ${m(2, 0, 4, 0)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${m(2, 0, 6, 0)};
        position: sticky;
        top: 0;
        background: ${(props) => (props.showBackground ? "#fff" : "transparent")};
        box-shadow: ${(props) => (props.showBackground ? "0px 16px 60px rgba(174, 177, 180, 0.6)" : "none")};
    }
`;

const navStyles = (theme: Theme) => css`
    ${w100};
    ${flex("center", "space-between")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(2)};
    }
`;

const justifyRight = css`
    justify-content: flex-end;
`;

const buttonsWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        width: unset;
    }

    & > div {
        ${mb(3)};

        &:last-of-type {
            ${mb(0)};
        }

        @media (min-width: ${theme.breakpoints.md}) {
            ${ml(3)};
            ${mb(0)};

            &:first-of-type {
                ${ml(0)};
            }
        }
    }
`;

const addressHolder = (theme: Theme) => css`
    ${pointer};
    ${flex("center")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt()};
    }
`;

const StyledButton = styled(Button)`
    padding: 0;
    min-width: auto;
`;

const showHeader = css`
    ${fadeInAnimation("0.2s")};
    display: block;
`;

const opacity0 = css`
    opacity: 0;
`;
import React, {useRef} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {display, fadeInAnimation, flex, m, mb, ml, mr, mt, pointer, pv, w100} from "@pg-design/helpers-css";
import {BrandBuildingCubeIcon, BrandMapPinIcon, BrandPropertyPlanIcon, GalleryOutlineIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";

interface IProps {
    offer: IPropertyDetailOffer;
    property?: IPropertyDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    // offer plans
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
    navBarContentStyle?: string;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyNavBar = React.memo((props: IProps) => {
    const {property, offer, onButtonClick} = props;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const wrapper = useRef<HTMLTableSectionElement>(null);
    const isNavBarSticky = useStickyIntersection(wrapper);

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const planImage = head(property?.plan_image_pages || [])?.image;

    const onShowPropertyGalleryClick = () => {
        props.onButtonClick(OfferModalActiveTab.GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowPropertyPlanClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_DETAIL, offer, offer.vendor);
        props.onButtonClick(
            OfferModalActiveTab.PLAN,
            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
        );
    };

    const renderHeader = () => {
        if (props.property) {
            return (
                <>
                    {isApartment ? "Apartament" : getOfferTypeNameCapital(props.offer.type)}, {props.property.number}, {props.property.rooms}{" "}
                    {roomPlural(props.property.rooms)}, {areaFormat(props.property.area, {precision: 2})}
                </>
            );
        }

        return <span className="bt">{props.offer.name}</span>;
    };

    const showPlanButton = !isMobile && offer.configuration.show_offer_plans && props.isPlanScriptLoading != null && props.onPlansOpenClick != null;

    return (
        <NavigationWrapper className={props.className} ref={wrapper} showBackground={isNavBarSticky}>
            <OneColumnGridHolder className={props.navBarContentStyle}>
                <OneColumnGridMain>
                    <nav css={[navStyles, !isNavBarSticky && justifyRight]}>
                        <div css={[opacity0, display("none"), isNavBarSticky && !isMobile && showHeader]}>
                            <Text as="h3" variant="headline_6">
                                {renderHeader()}
                            </Text>

                            <address css={addressHolder}>
                                <MapMarkerLocationIcon css={mr(2)} fill={theme.colors.gray["700"]} size="2.4" />
                                <Text color={theme.colors.gray[700]} variant="body_copy_2">
                                    {props.offer.address}
                                </Text>
                            </address>
                        </div>

                        <div css={buttonsWrapper}>
                            <Hidden on="desktop">
                                {property && isPropertyFavouriteButtonAvailable(property) && (
                                    <FavoriteButton css={[w100, mb(3)]} offer={offer} property={property} display={{type: "button", size: "m"}} />
                                )}
                                {property && (
                                    <div css={w100}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <StyledButton iconLeft={GalleryOutlineIcon} onClick={onShowPropertyGalleryClick} size="big" variant="brand_icon">
                                                Zobacz galerię
                                            </StyledButton>
                                        </div>
                                    </div>
                                )}
                            </Hidden>
                            <Hidden on="mobile">
                                {property && property.plan && (
                                    <div css={w100}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <StyledButton
                                                href={!planImage ? property.plan : undefined}
                                                iconLeft={BrandPropertyPlanIcon}
                                                onClick={planImage && onShowPropertyPlanClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_PLAN_BUTTON}
                                            >
                                                Zobacz rzut
                                            </StyledButton>
                                        </div>
                                    </div>
                                )}
                            </Hidden>

                            {showPlanButton && (
                                <div css={w100}>
                                    <StyledButton iconLeft={BrandBuildingCubeIcon} onClick={props.onPlansOpenClick} size="big" variant="brand_icon">
                                        {props.isPlanScriptLoading ? "loader" : "Plan pięter"}
                                    </StyledButton>
                                </div>
                            )}

                            <div css={w100}>
                                <StyledButton
                                    iconLeft={BrandMapPinIcon}
                                    onClick={() => {
                                        onButtonClick(
                                            OfferModalActiveTab.MAP,
                                            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        );
                                    }}
                                    size="big"
                                    variant="brand_icon"
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                >
                                    Zobacz na mapie
                                </StyledButton>
                            </div>
                        </div>
                    </nav>
                </OneColumnGridMain>
            </OneColumnGridHolder>
        </NavigationWrapper>
    );
});

const NavigationWrapper = styled.nav<{showBackground: boolean}>`
    display: block;
    z-index: 10; // z-index: we have relative positioned elements on the page, in case of property view, nav bar needs to have z-index: 2, because child components are using default slick slider arrows with index: 1
    width: 100%;
    ${m(2, 0, 4, 0)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${m(2, 0, 6, 0)};
        position: sticky;
        top: 0;
        background: ${(props) => (props.showBackground ? "#fff" : "transparent")};
        box-shadow: ${(props) => (props.showBackground ? "0px 16px 60px rgba(174, 177, 180, 0.6)" : "none")};
    }
`;

const navStyles = (theme: Theme) => css`
    ${w100};
    ${flex("center", "space-between")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(2)};
    }
`;

const justifyRight = css`
    justify-content: flex-end;
`;

const buttonsWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        width: unset;
    }

    & > div {
        ${mb(3)};

        &:last-of-type {
            ${mb(0)};
        }

        @media (min-width: ${theme.breakpoints.md}) {
            ${ml(3)};
            ${mb(0)};

            &:first-of-type {
                ${ml(0)};
            }
        }
    }
`;

const addressHolder = (theme: Theme) => css`
    ${pointer};
    ${flex("center")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt()};
    }
`;

const StyledButton = styled(Button)`
    padding: 0;
    min-width: auto;
`;

const showHeader = css`
    ${fadeInAnimation("0.2s")};
    display: block;
`;

const opacity0 = css`
    opacity: 0;
`;
